/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { RootTree, WithLatestStateDeviceNode } from './RootTree';
import { ROOT_GROUP_ID } from '../../../constants/group';
import { useCameraGroups } from './RootTree/use-camera-groups';
import { AdvanceSearch } from './AdvanceSearch';
import { CreateGroup } from '../Aggregated';
import { Button } from 'antd';
import { AddFolderIcon } from '../../../icons/AddFolder';

import './styles.scss';
import { AddCameraToGroup } from './AddCameraToGroup';
import { isEqualArray, mapToIds, normalizeIds } from '../Live/v2';
import { useDispatch, useSelector } from 'react-redux';
import { getRecoveryCameraPage } from '../../../redux/recovery/reducer';
import { updateRecoveryCameraPage } from '../../../redux/recovery/action';

export const CameraTree = forwardRef(
  (
    {
      namespace,
      onSelect,
      selectedId,
      setRecentItems,
      onSelectCamera: onSelectCameraProp,
      onUpdateCamera,
      onUpdateStream,
      updateReturn,
      selectedSerial,
      setAsCustomCamera,
      onPinCamera,
      getIsCameraPinned,
      customCamera,
    },
    ref,
  ) => {
    const [groupToAddCamera, setGroupToAddCamera] = useState();
    const {
      rootGroups,
      getChildren,
      getCameras,
      groupMap,
      addGroup,
      removeGroup,
      removeCamera,
      renameGroup,
      updateCamera,
      addCameras,
      markDirty,
    } = useCameraGroups({
      namespace,
    });

    const addedGroup = useRef({});

    const [expandedMap, setExpandedMap] = useState({});
    const [isAddingGroup, setIsAddingGroup] = useState(false);
    const [searchGroup, setSearchGroup] = useState(null);

    const isSearchMode = !!searchGroup || !!customCamera;
    const { expandedTreeGroupIds } = useSelector(getRecoveryCameraPage);
    const dispatch = useDispatch();

    useEffect(() => {
      if (updateReturn && updateReturn.isSuccess) {
        updateCamera(updateReturn.data.serial, updateReturn.data);
      }
    }, [updateReturn, updateCamera]);

    useImperativeHandle(ref, () => ({
      markDirty,
    }));

    const getIsExpanded = (groupId) => expandedMap[groupId] ?? false;

    const onExpand = useCallback(
      (groupId) => {
        setExpandedMap((prev) => {
          const tmp = { ...prev, [groupId]: !prev[groupId] };
          const nextIds = normalizeIds(mapToIds(tmp));
          if (!isEqualArray(nextIds, normalizeIds(expandedTreeGroupIds))) {
            dispatch(
              updateRecoveryCameraPage({
                expandedTreeGroupIds: nextIds,
              }),
            );
          }
          return tmp;
        });
      },
      [dispatch, expandedTreeGroupIds],
    );

    useEffect(() => {
      const normalized = normalizeIds(expandedTreeGroupIds);

      const newMap = normalized.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});

      const currentIds = normalizeIds(mapToIds(expandedMap));

      if (!isEqualArray(normalized, currentIds)) {
        setExpandedMap(newMap);
      }
    }, [expandedTreeGroupIds, expandedMap]);

    const addCameraToRecent = (camera) => {
      setRecentItems((prev) => {
        const newItems = prev.filter(
          (item) =>
            item.type !== 'camera' || item.data.serial !== camera.serial,
        );

        return [{ data: camera, type: 'camera' }, ...newItems];
      });
      // openMessage(NOTIFIES.INFO, 'Camera added to recent');
    };

    const onSelectCamera = (camera) => {
      onSelectCameraProp(camera);
    };

    const getShouldScrollAfterLoad = useCallback(
      (groupId) => {
        return searchGroup?.id === groupId;
      },
      [searchGroup],
    );

    const onSelectAdvanceSearchGroup = (group) => {
      const additionalExpand = group.parentPath.reduce((acc, id) => {
        acc[id] = true;

        return acc;
      }, {});

      setExpandedMap((prev) => ({ ...prev, ...additionalExpand }));
      onSelect(group);
      setSearchGroup(group);
    };

    const onAddGroupSuccess = (group) => {
      addedGroup.current[group.id] = group;
      addGroup(group);
    };

    const onRemoveGroupSuccess = (groupId) => {
      if (groupId === searchGroup?.id) {
        setSearchGroup(null);
      }

      if (groupId === selectedId) {
        onSelect({ id: ROOT_GROUP_ID });
      }

      removeGroup(groupId);
    };

    const onRemoveCameraSuccess = (groupId, serial) => {
      removeCamera(groupId, serial);
    };

    const onRenameSuccess = (groupId, name) => {
      renameGroup(groupId, name);
    };

    const onCameraAdded = (groupId, newCameras) => {
      setGroupToAddCamera(null);
      addCameras(groupId, newCameras);

      if (groupToAddCamera?.id === groupId) {
        setGroupToAddCamera(null);
      }
    };

    const getIsCameraSelected = (serial) => {
      return selectedSerial === serial;
    };

    const getAllowModify = (id) => {
      if (searchGroup?.id === id) {
        return !!searchGroup?.permissionGroup || !!addedGroup.current[id];
      }

      return (
        !!groupMap.current[id]?.permissionGroup || !!addedGroup.current[id]
      );
    };

    return (
      <div className="camera-page-tree">
        <div className="camera-tree-header">
          <AdvanceSearch
            namespace={namespace}
            onSelectCamera={setAsCustomCamera}
            onSelectGroup={onSelectAdvanceSearchGroup}
          />

          <Button
            onClick={() => setIsAddingGroup(true)}
            icon={<AddFolderIcon width={20} height={20} />}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          ></Button>
        </div>
        <div style={{ height: 8 }}></div>
        {isSearchMode && (
          <div className="back-button">
            <a
              onClick={() => {
                setSearchGroup(null);
                setAsCustomCamera(null);
              }}
              style={{
                color: '#0e9347',
                textDecoration: 'underline',
                display: 'block',
                height: 24,
              }}
            >
              Back
            </a>
          </div>
        )}
        <div
          className={
            isSearchMode
              ? 'camera-page-tree-content search-mode'
              : 'camera-page-tree-content'
          }
          onClick={console.log}
        >
          {isAddingGroup && (
            <CreateGroup
              namespace={namespace}
              level={0}
              parentGroupId={ROOT_GROUP_ID}
              onCancel={() => setIsAddingGroup(false)}
              onSuccess={(newGroup) => {
                setIsAddingGroup(false);
                onAddGroupSuccess(newGroup);
              }}
              placeholder="New group"
            />
          )}
          {customCamera && (
            <WithLatestStateDeviceNode
              pinned={getIsCameraPinned(customCamera.serial)}
              onPin={(pinned) => onPinCamera(customCamera, pinned)}
              name={customCamera.name}
              serial={customCamera.serial}
              onSelect={onSelectCamera}
              isSelected={getIsCameraSelected(customCamera.serial)}
              camera={customCamera}
              onUpdate={onUpdateCamera}
              onUpdateStream={onUpdateStream}
              state={customCamera.state}
            />
          )}
          {!customCamera && (
            <RootTree
              namespace={namespace}
              onSelect={onSelect}
              selectedId={selectedId}
              rootGroups={searchGroup ? [searchGroup] : rootGroups}
              getChildren={getChildren}
              getCameras={getCameras}
              groupMap={groupMap}
              onSelectCamera={(cam, groupId) => {
                onSelectCamera(cam);
                onSelect({ id: groupId });
                addCameraToRecent(cam);
              }}
              getIsExpanded={getIsExpanded}
              onExpand={onExpand}
              onAddGroupSuccess={onAddGroupSuccess}
              onRemoveGroupSuccess={onRemoveGroupSuccess}
              onRemoveCameraSuccess={onRemoveCameraSuccess}
              onUpdateCamera={onUpdateCamera}
              onUpdateStream={onUpdateStream}
              onRenameSuccess={onRenameSuccess}
              onAddCameraToGroup={setGroupToAddCamera}
              getIsCameraSelected={getIsCameraSelected}
              getShouldScrollAfterLoad={getShouldScrollAfterLoad}
              getAllowModify={getAllowModify}
              onPinCamera={onPinCamera}
              getIsCameraPinned={getIsCameraPinned}
            />
          )}
          {groupToAddCamera && (
            <AddCameraToGroup
              groupId={groupToAddCamera.id}
              groupName={groupToAddCamera.name}
              getGroupCameras={getCameras}
              onSuccess={onCameraAdded}
              onClose={() => setGroupToAddCamera(null)}
              namespace={namespace}
            />
          )}
        </div>
      </div>
    );
  },
);
